import { useState, useCallback } from 'react';
import { useCreateScheduleShift } from './useCreateScheduleShift';
import { lookupErrorCopy } from '../lib/errorMap';

interface CreateShiftData {
  business_id: string;
  user_id: string;
  date: string;
  start_time: string;
  end_time: string;
  position?: string;
  location?: string;
  notes?: string;
  created_by: string;
}

export function useCreateShift() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createScheduleShift = useCreateScheduleShift();

  const createShift = useCallback(async (data: CreateShiftData): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      if (!data.user_id) { setError('Team member is required'); setLoading(false); return false; }
      if (!data.date) { setError('Date is required'); setLoading(false); return false; }
      if (data.start_time >= data.end_time) { setError('End time must be after start time'); setLoading(false); return false; }

      // Canonical-path lift per Q1 lock + INV-Schedule-3: schedule_shifts is the
      // source of truth; sync_schedule_shift_to_entry_trg auto-creates the
      // schedule_entries projection. Caller doesn't author schedule_entries directly.
      const res = await createScheduleShift({
        userId: data.user_id,
        date: data.date,
        startTime: data.start_time,
        endTime: data.end_time,
        position: data.position,
        location: data.location,
        notes: data.notes,
        source: 'manual',
        idempotencyKey: crypto.randomUUID(),
      });
      if (!res.success) {
        setError(lookupErrorCopy('create_schedule_shift', res.error.code));
        setLoading(false);
        return false;
      }
      setLoading(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create shift');
      setLoading(false);
      return false;
    }
  }, [createScheduleShift]);

  return { createShift, loading, error };
}
